/* eslint-disable react/destructuring-assignment */
import { CloseCircleFilled, InfoCircleFilled, InfoCircleOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import {
  Button,
  message,
  Modal,
  Spin,
  Switch,
  Tag,
  Segmented as ThreeWayToggle,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { capitalize, isEmpty, isString } from "lodash";
import React, { useMemo, useState, useEffect } from "react";
import { LuExternalLink } from "react-icons/lu";
import { useDispatch } from "react-redux";
import { MODIFY_EMAIL_CONSENT, MODIFY_LEAD_AUTO_RESPONDER } from "../../../graphql/mutation";
import useLang from "../../../hooks/useLang";
import { NewTabRedirect } from "../../../icons/js/NewTabRedirectionIcon";
import TickIcon from "../../../icons/js/TickIcon";
import PlayConsentIcon from "../../../icons/js/PlayConsentIcon";
import PauseConsentIcon from "../../../icons/js/PauseConsentIcon";
import { AccessPermissionAction } from "../../../library/constants";
import { closeDialog, openDialog, checkAccessPermission } from "../../../library/helpers";
import {
  mergeLeadDetail,
  setAutoResponseCampaign,
  updateLeadInList,
} from "../../../redux/slices/leadSlice";
import TimerPauseIcon from "../../../icons/js/TimerPauseIcon";
// Importing this also registers dayjs's utc/timezone plugins, which
// dayjs.utc() below relies on.
import { DATE_TIME_FORMATS } from "../../../utils/dateTimes";

/* The three Auto Response states, in display order.
 *
 * Only the selected segment shows its text label — the other two collapse to
 * their icon alone, which is what keeps the control narrow enough for the lead
 * profile rail. `langKey` resolves against `en.json`.
 */
const AUTO_RESPONSE_OPTIONS = [
  {
    value: "ENROLLED",
    langKey: "autoResponseEnrolled",
    Icon: PlayConsentIcon,
    activeIconClass: "text-[#389E0D]",
    activeBgClass: "bg-[#D9F7BE]",
    tooltip: "Enrolling the auto-responder will automatically send replies to customers.",
  },
  {
    value: "PAUSED",
    langKey: "autoResponsePaused",
    Icon: TimerPauseIcon,
    activeIconClass: "text-[#0958D9]",
    activeBgClass: "bg-[#D5EDFF]",
    tooltip:
      "Pausing the auto-responder will temporarily stop sending automatic replies to customers.",
  },
  {
    value: "DISENROLLED",
    langKey: "autoResponseDisenrolled",
    Icon: PauseConsentIcon,
    activeIconClass: "text-[#D46B08]",
    activeBgClass: "bg-[#FFF1B8]",
    tooltip: "Disenrolling the lead will remove them from the auto-responder campaign.",
  },
];

const LeadConsent = props => {
  // `refetchMessages` reloads the conversation thread. Every consent change is
  // recorded as a CONSENT_UPDATE message, so without it the new timeline row
  // only appears on the next poll or lead switch.
  const {
    handleOptedOut,
    selectedLead,
    handleSendConsentClick,
    user,
    consentLoader,
    refetchMessages = () => {},
  } = props;

  const [lang] = useLang();
  const [selectedConsent, setselectedConsent] = useState("sms");

  const smsConsent = selectedLead?.textConsentStatus;
  // Email consent (EAI-1666) is a nullable boolean on the lead: null = never
  // recorded (implied consent), true = opted in, false = opted out. Keep null
  // distinct from false -- `|| null` would collapse the two.
  const emailConsent = selectedLead?.emailConsent ?? null;

  const canModifyOttoResponder = checkAccessPermission(
    AccessPermissionAction.LEAD_MANAGEMENT_ALLOW_USER_MODIFY_OTTO_RESPONDER_STATUS
  );
  const canModifyEmailConsent = checkAccessPermission(
    AccessPermissionAction.LEAD_MANAGEMENT_ALLOW_USER_MODIFY_EMAIL_CONSENT
  );

  const status = user?.company?.isOptinConsentMethod;
  const colorCodes = (consent, status) => {
    if (consent === "ACCEPTED") {
      return {
        bg: "bg-[#ECFDF5]",
        border: "border-[#10B981]",
        text: "text-[#10b981]",
        consent: "Implied Consent",
        icon: <TickIcon />,
      };
    }
    if (consent === "PENDING" && status) {
      return {
        bg: "bg-[#dbd0ae]",
        border: "border-[#ffbf00]",
        text: "text-[#ffbf00]",
        consent: "Pending",
        icon: <InfoCircleFilled />,
      };
    }
    if (consent === "PENDING" && !status) {
      return {
        bg: "bg-[#ECFDF5]",
        border: "border-[#10B981]",
        text: "text-[#10b981]",
        consent: "Implied Consent",
        icon: <TickIcon />,
      };
    }
    if (consent === "DECLINED") {
      return {
        bg: "bg-[#FEF2F2]",
        border: "border-[#EF4444]",
        text: "text-[#EF4444]",
        consent: "Opted-Out",
        icon: <CloseCircleFilled className="text-[#EF4444] text-[20px]" />,
      };
    }
    return {
      bg: "bg-gray-50",
      border: "border-gray-500",
      text: "text-gray-500",
      consent: "Send Consent",
      icon: <InfoCircleFilled />,
    };
  };
  const consentDataSMS = useMemo(() => colorCodes(smsConsent, status), [smsConsent, status]);

  const [modifyEmailConsent, { loading: emailConsentLoader }] = useMutation(MODIFY_EMAIL_CONSENT);
  const dispatchRoot = useDispatch();

  // `nextConsent` is the value to store: true = opt in, false = opt out. The
  // backend re-checks the permission and rejects the mutation on its own, so a
  // user without it gets a server error even if the UI were bypassed.
  const handleEmailConsent = async nextConsent => {
    try {
      const res = await modifyEmailConsent({
        variables: {
          leadId: selectedLead.id,
          emailConsent: nextConsent,
        },
      });
      const result = res?.data?.modifyEmailConsent;
      if (result?.statusCode === 200) {
        message.success(result.message);
        const updatedLead = result.lead;
        if (updatedLead?.id) {
          dispatchRoot(mergeLeadDetail(updatedLead));
          dispatchRoot(updateLeadInList(updatedLead));
        }
        refetchMessages();
        closeDialog("confirmDialog");
      } else {
        message.error(result?.message || "Unable to update email consent.");
      }
    } catch (err) {
      message.error(err?.message || "Unable to update email consent.");
    }
  };

  return (
    <div className="flex-col-centered gap-1 items-start">
      <div className="flex-row-centered mb-1 items-center gap-1">
        <Tag
          onClick={() => setselectedConsent("sms")}
          className={`${
            selectedConsent === "sms" ? "consent-tag-selected" : "consent-tag"
          } cursor-pointer`}
        >
          SMS Consents
        </Tag>
        <Tag
          onClick={() => setselectedConsent("email")}
          className={`${
            selectedConsent === "email" ? "consent-tag-selected" : "consent-tag"
          } cursor-pointer`}
        >
          Email Consents
        </Tag>
      </div>
      {selectedConsent === "sms" ? (
        <div className="grid grid-cols-2 flex-row-centered">
          {consentLoader ? (
            <Spin spinning tip="Sending Consent request..." />
          ) : (
            <Tag
              className={`h-full flex-col-centered cursor-pointer items-center justify-center ${consentDataSMS?.text} gap-1 ${consentDataSMS?.bg} ${consentDataSMS?.border} `}
              onClick={e => {
                if (smsConsent !== "DECLINED") {
                  handleSendConsentClick(e, selectedLead?.textConsentStatus);
                } else {
                  return message.error(`${lang.smsOptedOutErrorOnConsentRequest}`);
                }
              }}
            >
              {consentDataSMS?.icon}
              {consentDataSMS?.consent}
            </Tag>
          )}
          <ConsentJSX
            handleOptedOut={handleOptedOut}
            selectedConsent={selectedConsent}
            status={status}
            consent={smsConsent !== "DECLINED"}
            selectedLead={selectedLead}
            lang={lang}
            user={user}
            sendConsent={e => handleSendConsentClick(e, selectedLead?.textConsentStatus)}
            canModifyOttoResponder={canModifyOttoResponder}
            refetchMessages={refetchMessages}
          />
        </div>
      ) : (
        <EmailConsentJSX
          emailConsent={emailConsent}
          emailConsentDate={selectedLead?.emailConsentDate}
          loading={emailConsentLoader}
          canModifyEmailConsent={canModifyEmailConsent}
          onChangeConsent={handleEmailConsent}
        />
      )}
      {/* Otto campaign controls the SMS campaign, and the Email tab design is a
          single opt-in row, so this stays on the SMS tab only. */}
      {selectedConsent === "sms" && (
        <Button
          type="primary"
          onClick={() =>
            openDialog("campaignToggle", {
              dialogHeader: "Turn off campaign message",
              selectedLead,
              refetchMessages,
            })
          }
        >
          Activate/Deactivate Otto Campaign <LuExternalLink />{" "}
        </Button>
      )}
    </div>
  );
};

export default LeadConsent;

/**
 * Email consent (EAI-1666). A single opt-in switch with a status line, per design.
 *
 * `emailConsent` is a nullable boolean: null = never recorded, true = opted in,
 * false = opted out. Only an explicit false blocks sending, so null reads as ON —
 * the switch answers "will emails send?", which is what the label promises.
 *
 * @param {{emailConsent: boolean | null, emailConsentDate?: string, loading?: boolean,
 *   canModifyEmailConsent?: boolean, onChangeConsent: (next: boolean) => any}} props
 */
export function EmailConsentJSX({
  emailConsent,
  emailConsentDate,
  loading,
  canModifyEmailConsent,
  onChangeConsent,
}) {
  const isOptedOut = emailConsent === false;
  const willSend = !isOptedOut;

  const tooltip = isOptedOut
    ? "Opted out. No emails will send, including automated follow-ups."
    : `${
        emailConsent === true ? "Opted in" : "Implied consent"
      }. Emails will send, including automated follow-ups.`;

  // Provenance ("via web form", "by customer") is intentionally absent: nothing
  // records how email consent was captured. Only the value and its timestamp
  // exist, so that is all this states.
  const statusLine = () => {
    if (emailConsent === null || emailConsent === undefined) {
      return "Implied consent";
    }
    const label = isOptedOut ? "Opted out" : "Opted in";
    // email_consent_date is stored as naive UTC, so convert before formatting —
    // same as the email list and lead list do (dayjs.utc(...).local()). A bare
    // dayjs() parse would render the UTC clock time as if it were local.
    const on = emailConsentDate
      ? dayjs.utc(emailConsentDate).local().format(DATE_TIME_FORMATS.MMM_DD_YYYY_COMMA_hh_mm_A)
      : "";
    return on ? `${label} · ${on}` : label;
  };

  // Layout per design: the switch sits beside a single column holding the label
  // and the status line, so the timestamp shares a left edge with "Email opt-in"
  // rather than with the switch. `flex flex-row` is spelled out instead of
  // `flex-row-centered` because that helper lives in @layer utilities alongside
  // `items-start` — same layer means source order, not intent, would decide the
  // winner. The switch is aligned to the label line (items-start), nudged 2px to
  // optically centre the 16px control against the 20px line box.
  return (
    <div className="flex flex-row gap-2 items-start py-4 text-sm text-gray-700">
      <Switch
        size="small"
        checked={willSend}
        loading={loading}
        disabled={!canModifyEmailConsent}
        className={`mt-0.5${!canModifyEmailConsent ? " cursor-not-allowed" : ""}`}
        onChange={checked => {
          if (checked === false) {
            openDialog("confirmDialog", {
              label: "Are you sure you want to Opt-out this lead from email?",
              handleConfirm: () => onChangeConsent(false),
            });
          } else {
            onChangeConsent(true);
          }
        }}
      />
      <div className="flex-col-centered gap-1 items-start">
        <span className="flex-row-centered gap-1 font-medium">
          Email opt-in
          <Tooltip title={tooltip}>
            <InfoCircleOutlined className="w-3 h-3" />
          </Tooltip>
        </span>
        <span className="text-xs text-gray-500">{statusLine()}</span>
      </div>
    </div>
  );
}

function ConsentJSX({
  selectedConsent,
  selectedLead,
  lang,
  user,
  consent,
  handleOptedOut,
  sendConsent,
  canModifyOttoResponder,
  refetchMessages = () => {},
}) {
  const dispatch = useDispatch();
  const [ottoResponderState, setottoResponderState] = useState(
    selectedLead?.campaignAutoResponse || "DISENROLLED"
  );
  useEffect(() => {
    setottoResponderState(selectedLead?.campaignAutoResponse ?? "DISENROLLED");
  }, [selectedLead?.campaignAutoResponse]);
  const settings = useMemo(() => {
    if (selectedLead?.location?.settings) {
      return isString(selectedLead?.location?.settings)
        ? JSON.parse(selectedLead?.location?.settings)
        : selectedLead?.location?.settings;
    }
  }, [selectedLead]);
  const [modifyLeadAutoResponder] = useMutation(MODIFY_LEAD_AUTO_RESPONDER);
  const handleAutoResponse = async value => {
    const status = value;
    const res = await modifyLeadAutoResponder({
      variables: {
        leadId: selectedLead.id,
        status,
      },
    });
    if (res.data.modifyLeadAutoResponder.statusCode === 200) {
      message.success(res.data.modifyLeadAutoResponder.message);
      dispatch(setAutoResponseCampaign(status));
      dispatch(updateLeadInList({ id: selectedLead.id, campaignAutoResponse: status }));
      setottoResponderState(value);
      refetchMessages();
      closeDialog("confirmDialog");
    } else {
      message.error(res.data.modifyLeadAutoResponder.message);
    }
  };
  return (
    <div className="flex-col-centered gap-3">
      <div className="flex-row-centered gap-1 items-center text-sm text-gray-700">
        <Switch
          disabled={!consent}
          size="small"
          checked={consent}
          onChange={(checked, e) => {
            if (checked === false) {
              openDialog("confirmDialog", {
                label: "Are you sure you want to Opt-out this lead?",
                handleConfirm: handleOptedOut,
              });
            } else {
              sendConsent(e);
            }
          }}
          className={!canModifyOttoResponder ? "cursor-not-allowed" : ""}
        />{" "}
        <span className="flex-row-centered items-center gap-1">
          {selectedConsent.toUpperCase()} Status{" "}
          <Tooltip
            title={`This will stop you from sending any more ${capitalize(
              selectedConsent
            )} messages to the customer, including the automated messages (appointments, reminders, campaigns).`}
          >
            <InfoCircleOutlined className="w-3 h-3" />{" "}
          </Tooltip>
        </span>
      </div>
      <div className="flex-col-centered gap-1 items-start text-sm text-gray-700">
        <span className="flex-row-centered items-center gap-1">
          {lang.autoResponseToggle}
          <Tooltip title="This will allow you to control the sending of automatic responses by the system.">
            <InfoCircleOutlined className="w-3 h-3" />
          </Tooltip>
        </span>
        <ThreeWayToggle
          value={ottoResponderState}
          // Shell, segment radii and label centring live in `.auto-response-toggle`
          // (src/styles/main.css) — it has to override antd's own label layout.
          className={`auto-response-toggle${!canModifyOttoResponder ? " cursor-not-allowed" : ""}`}
          onChange={value => {
            if (
              value === ottoResponderState ||
              !canModifyOttoResponder ||
              !settings?.AUTO_RESPONSE_ENABLED
            ) {
              return;
            }
            openDialog("confirmDialog", {
              label: `${
                value === "ENROLLED"
                  ? lang.startAutoResponderModalTitle
                  : value === "PAUSED"
                  ? lang.pauseAutoResponderModalTitle
                  : lang.stopAutoResponderModalTitle
              }`,
              content: `${
                value === "ENROLLED"
                  ? lang.startAutoResponderModalBody
                  : value === "PAUSED"
                  ? lang.pauseAutoResponderModalBody
                  : lang.stopAutoResponderModalBody
              }`,
              handleConfirm: () => handleAutoResponse(value),
            });
          }}
          disabled={!settings?.AUTO_RESPONSE_ENABLED || !canModifyOttoResponder}
          options={AUTO_RESPONSE_OPTIONS.map(
            ({ value, langKey, Icon, activeIconClass, activeBgClass, tooltip }) => {
              const isActive = ottoResponderState === value;
              return {
                value,
                label: (
                  <Tooltip title={tooltip}>
                    <span className="flex items-center gap-1 leading-none">
                      {/*
                        Fixed 16px box: the glyphs are 11x14 / 16x16 / 12x12, so without
                        it each state would centre on a different footprint. The inline
                        style resets the `marginTop: 6-8px` each icon file hardcodes for
                        the old icon-only layout (restProps spreads after their `style`).
                      */}
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        <Icon
                          className={isActive ? activeIconClass : "text-black/[0.88]"}
                          style={{ display: "block", marginTop: 0 }}
                        />
                      </span>
                      {isActive ? lang[langKey] : null}
                    </span>
                  </Tooltip>
                ),
                className: isActive ? activeBgClass : "",
              };
            }
          )}
        />
      </div>
      <div className="flex-row-centered gap-1 items-center text-sm text-gray-700">
        {/* <Switch
          onChange={checked => {
            if (!checked) {
              openDialog("campaignToggle", {
                dialogHeader: "Turn off campaign message",
                selectedLead,
              });
            }
          }}
          size="small"
        />
        <span className="flex-row-centered items-center gap-1">
          {lang.ottoCampaignToggle}{" "}
          <Tooltip title={lang.ottoCampaignToggleTooltip}>
            <InfoCircleOutlined className="w-3 h-3" />
          </Tooltip>
        </span> */}
      </div>
    </div>
  );
}

/**
 *
 * @param {{open:boolean,setOpen:import("react").Dispatch<import("react").SetStateAction<boolean>>,title:string,okText:import("react").ReactNode,cancelText:import("react").ReactNode, onOk:()=>any,description:string,loader:boolean,okClassName:string}} props
 * @returns
 */
export const CancelScheduleConfirmation = props => {
  const handleCancel = () => props.setOpen(false);
  return (
    <Modal
      title={props.title}
      open={props.open}
      onCancel={handleCancel}
      footer={
        <div className="flex-row-centered justify-start items-center gap-2">
          <Button
            className={props.okClassName}
            loading={props.loader}
            type="primary"
            onClick={props.onOk}
          >
            {props.okText || "Yes"}
          </Button>
          <Button onClick={handleCancel}>{props.cancelText || "cancel"}</Button>
        </div>
      }
    >
      <span>{props.description}</span>
    </Modal>
  );
};
